// @flow
import React from 'react'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import Snackbar from '@material-ui/core/Snackbar'
import TextField from '@material-ui/core/TextField'
import GeoJSON from 'ol/format/GeoJSON'
import OlFeature from 'ol/Feature'
import OlMap from 'ol/Map'
import OlVectorLayer from 'ol/layer/Vector'
import OlVectorSource from 'ol/source/Vector'
import Circle from 'ol/style/Circle'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import Text from 'ol/style/Text'
import OlModify from 'ol/interaction/Modify'
import OlTranslate from 'ol/interaction/Translate'
import { compare } from 'er2_web_utils'
import { getLayer, getLayers } from 'catena/er2_map_userlayers/js/utils'
import * as ids from '../constants/ids'
import * as olUtils from '../util/ol'

function getDrawPromptMessage(msgType) {
    switch (msgType) {
    case ids.GEO_BAR_DIG_METHOD_DRAW_POINT:
        return 'Click to draw a point on the map'
    case ids.GEO_BAR_DIG_METHOD_DRAW_LINE:
        return 'Click to begin drawing a line on the map'
    case ids.GEO_BAR_DIG_METHOD_DRAW_POLYGON:
        return 'Click to begin drawing a polygon on the map'
    case ids.GEO_BAR_DIG_METHOD_DRAW_RECTANGLE:
        return 'Click to begin drawing a rectangle on the map'
    default:
        return 'Click to draw a point on the map'
    }
}

type Props = {
    actions: {
        onChangeInput: Function,
        onClickInlineHelp: Function,
        onGeoBarFetchKnownBoundary: Function,
        onGeoBarFetchErrorRendered: Function,
        onGeoBarFetchKnownBoundaries: Function,
        onGeoBarResultRendered: Function,
        onGeoBarSubmitDigitize: Function,
        onGeoBarSelectLayer: Function,
        onUpdateUserLayers: Function,
    },
    olMap: OlMap,
    mapsources: {},
    token: {
        value: string,
        expires: number,
    },
    theme?: {},
}

type StateProps = {
    displayCreate: boolean,
    drawnFeature: OlFeature,
    drawnFeatureName: string,
    drawPromptRendered: boolean,
    editingLayer: string,
    errorRendered: boolean,
    isModified: boolean,
    helpActive: boolean,
    loadingFeatures: boolean,
    method: string,
    newLayerName: string,
    newLayerType: string,
    resultRendered: boolean,
    savingFeatures: boolean,
    schema: {
        properties: {
            name: string,
        },
    },
}

class GeoBarDigitize extends React.Component<Props, StateProps> {
    constructor(props) {
        super(props)
        this.state = {
            displayCreate: true,
            drawnFeature: null,
            drawnFeatureName: '',
            drawPromptRendered: true,
            editingLayer: '__new__',
            errorRendered: true,
            isModified: false,
            helpActive: false,
            loadingFeatures: false,
            method: 'modify',
            newLayerName: '',
            newLayerType: 'Polygon',
            resultRendered: true,
            savingFeatures: false,
            schema: {
                properties: {
                    name: 'str',
                },
            },
        }
    }
    componentDidMount() {
        this.vectorSource = new OlVectorSource({
            projection: 'EPSG:4326',
        })
        this.vectorLayer = new OlVectorLayer({
            source: this.vectorSource,
            style: (feat, res) => this.getStyle(feat, res),
            zIndex: 1,
        })

        this.props.olMap.addLayer(this.vectorLayer)
        this.props.olMap.render()
    }

    componentDidUpdate(prevProps, prevState) {
        this.updateMapInteractions(prevState)
        this.updateFeatures(prevState)
    }

    componentWillUnmount() {
        if (this.drawInteraction) {
            this.props.olMap.removeInteraction(this.drawInteraction)
        }
        this.props.olMap.removeLayer(this.vectorLayer)
    }

    // Shapes that don't require buffers can be handled here.
    onDrawShape(evt) {
        if (this.state.drawnFeature) {
            this.vectorSource.removeFeature(this.state.drawnFeature)
        }
        this.vectorSource.addFeature(evt.feature)
        this.setState({ drawnFeature: evt.feature })
    }

    getStyle(feature, resolution) {
        const label = feature.get('name')
        let textStyle
        if (label) {
            textStyle = new Text({
                fill: new Fill(olUtils.styles.text.fill),
                font: '14px "Roboto Condensed", sans-serif',
                placement: 'point',
                stroke: new Stroke(olUtils.styles.text.stroke),
                text: label,
            })
        }
        return olUtils.getOlStyles({
            image: new Circle({
                radius: 7,
                fill: new Fill({ color: 'black' }),
                stroke: new Stroke({
                    color: [255, 0, 0], width: 2,
                }),
            }),
            text: textStyle,
        })
    }

    activateDrawTool() {
        const drawToolSelected = this.state.method
        this.props.olMap.removeInteraction(this.modify)
        this.props.olMap.removeInteraction(this.translate)
        if (drawToolSelected === ids.GEO_BAR_DIG_METHOD_DRAW_POINT) {
            this.drawInteraction = olUtils.makeDrawPointInteraction()
            this.drawInteraction.on('drawend', evt => this.onDrawShape(evt))
            this.props.olMap.addInteraction(this.drawInteraction)
        } else if (drawToolSelected === ids.GEO_BAR_DIG_METHOD_DRAW_LINE) {
            this.drawInteraction = olUtils.makeDrawLineInteraction()
            this.drawInteraction.on('drawend', evt => this.onDrawShape(evt))
            this.props.olMap.addInteraction(this.drawInteraction)
        } else if (drawToolSelected === ids.GEO_BAR_DIG_METHOD_DRAW_POLYGON) {
            this.drawInteraction = olUtils.makeDrawPolygonInteraction()
            this.drawInteraction.on('drawend', evt => this.onDrawShape(evt))
            this.props.olMap.addInteraction(this.drawInteraction)
        } else if (drawToolSelected === ids.GEO_BAR_DIG_METHOD_DRAW_RECTANGLE) {
            this.drawInteraction = olUtils.makeDrawRectangleInteraction()
            this.drawInteraction.on('drawend', evt => this.onDrawShape(evt))
            this.props.olMap.addInteraction(this.drawInteraction)
        } else if (drawToolSelected === ids.GEO_BAR_DIG_METHOD_DRAW_CIRCLE) {
            this.drawInteraction = olUtils.makeDrawCircleInteraction()
            this.drawInteraction.on('drawend', evt => this.onDrawShape(evt))
            this.props.olMap.addInteraction(this.drawInteraction)
        } else if (drawToolSelected === 'modify') {
            if (!this.modify) {
                this.modify = new OlModify({ source: this.vectorSource })
                this.translate = new OlTranslate({ source: this.vectorSource })
                this.modify.on('modifyend', (e) => {
                    this.setState({ isModified: true })
                })
                this.translate.on('translateend', (e) => {
                    this.setState({ isModified: true })
                })
            }
            this.props.olMap.addInteraction(this.translate)
            this.props.olMap.addInteraction(this.modify)
        }
    }

    updateMapInteractions(prevState) {
        const drawToolChanged = prevState.method !== this.state.method
        const drawToolActivated = !this.drawInteraction && this.state.method
        const drawToolDeactivated = this.drawInteraction && !this.state.method

        // Add and remove map interactions
        if (drawToolActivated) {
            this.activateDrawTool()
        } else if (drawToolChanged) {
            // Recreate the draw tool in case it wasn't destroyed from an earlier draw,
            // e.g. if a new method was selected while the draw tool was still active
            if (this.drawInteraction) {
                this.props.olMap.removeInteraction(this.drawInteraction)
            }
            this.activateDrawTool()
        } else if (drawToolDeactivated) {
            this.props.olMap.removeInteraction(this.drawInteraction)
            this.drawInteraction = undefined
        }
    }

    addFeature = () => {
        // Set drawnFeature to null so it won't get removed at next feature add.
        if (this.state.drawnFeatureName) {
            this.state.drawnFeature.set('name', this.state.drawnFeatureName)
        }
        this.setState({ drawnFeature: null, isModified: true })
    }

    cancelEdits = () => {
        this.vectorSource.clear()
        this.setState({ drawnFeature: null, isModified: false, loadingFeatures: false, savingFeatures: false })
        if (this.state.editingLayer !== '__new__') {
            this.loadFeatures()
        }
    }

    createNewLayer = (features) => {
        let fetchError = false
        const token = this.props.token.value
        this.setState({ savingFeatures: true, error: undefined })
        fetch(`/er2_map/create_map_layer/?token=${token}`, {
            body: JSON.stringify({
                features,
                layer_name: this.state.newLayerName,
                type: this.state.newLayerType,
            }),
            method: 'POST',
        }).then((response) => {
            fetchError = !response.ok
            return response.json()
        }).then((json) => {
            if (fetchError) {
                console.error(json)
                this.setState({ savingFeatures: false, error: json, errorRendered: false })
            } else {
                this.setState({ savingFeatures: false, editingLayer: json.new_layer.id })
                if (this.props.actions.onUpdateUserLayers) this.props.actions.onUpdateUserLayers()
            }
        })
    }

    loadFeatures() {
        let fetchError = false
        const token = this.props.token.value
        this.setState({ isModified: false, loadingFeatures: true, savingFeatures: false })
        fetch(`/er2_map/get_layer_attributes/?token=${token}`, {
            body: JSON.stringify({
                layer_id: this.state.editingLayer,
            }),
            method: 'POST',
        }).then((response) => {
            fetchError = !response.ok
            return response.json()
        }).then((json) => {
            if (fetchError) {
                this.setState({ loadingFeatures: false })
            } else {
                const rows = json.attributes
                this.setState({ loadingFeatures: false, schema: json.schema })
                this.setFeatures(rows)
            }
        })
    }

    save = () => {
        const geoJSON = new GeoJSON({ dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' })
        const features = this.vectorSource.getFeatures().map(f => geoJSON.writeFeature(f))

        this.setState({ savingFeatures: true, error: undefined })
        if (this.state.editingLayer === '__new__') {
            this.createNewLayer(features)
        } else {
            let fetchError = false
            const token = this.props.token.value
            fetch(`/er2_map/set_layer_attributes/?token=${token}`, {
                body: JSON.stringify({
                    layer_id: this.state.editingLayer,
                    features,
                }),
                method: 'POST',
            }).then((response) => {
                fetchError = !response.ok
                return response.json()
            }).then((json) => {
                if (fetchError) {
                    this.setState({ savingFeatures: false, error: json, errorRendered: false })
                    console.error(json)
                } else {
                    if (this.props.actions.onUpdateUserLayers) this.props.actions.onUpdateUserLayers()
                    this.loadFeatures()
                }
            })
        }
    }

    setFeatures(rows) {
        const geoJSON = new GeoJSON({ dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' })
        const features = rows.map((f => geoJSON.readFeature(f)))
        this.vectorSource.clear()
        this.vectorSource.addFeatures(features)
        const extent = this.vectorSource.getExtent()
        this.props.olMap.getView().fit(extent, this.props.olMap.getSize())
    }

    submitButtonIsEnabled() {
        let enabled = this.state.isModified
        // If new layer mode, then there must be a layer name and an edit
        if (this.state.editingLayer === '__new__') {
            enabled = enabled && this.state.newLayerName
        }
        return !this.isWaiting() && enabled
    }

    /**
     * Check if the layer's features need to be updated.
     * @param nextProps
     */
    updateFeatures(prevState) {
        const editingLayerChanged = prevState.editingLayer !== this.state.editingLayer
        if (editingLayerChanged && !prevState.loadingFeatures && this.state.editingLayer !== '__new__') {
            this.loadFeatures()
        }
    }

    isWaiting() {
        return this.state.loadingFeatures || this.state.savingFeatures
    }

    renderNotifications(theme) {
        const layerName = this.state.result ? this.state.editingLayer : ''
        const message = layerName ? `Result saved to ${layerName}` : 'No result found for this operation'
        const drawMessage = this.state.method ? getDrawPromptMessage(this.state.method) : ''

        // Check for errors
        const errorMessage = this.state.error ? `Sorry, got an error: ${this.state.error.exception.message}` : false

        return (<div>
            <Snackbar
                open={this.state.resultRendered === false}
                autoHideDuration={6000}
                onClose={() => {
                    this.setState({ resultRendered: true })
                }}
                message={<span id="message-id">{message}</span>}
            />
            <Snackbar
                open={this.state.drawPromptRendered === false}
                autoHideDuration={6000}
                onClose={() => this.setState({ drawPromptRendered: true })}
                message={<span id="message-id">{drawMessage}</span>}
            />
            <Snackbar
                open={this.state.errorRendered === false}
                onClose={() => this.setState({ errorRendered: true })}
                message={<span id="message-id">{errorMessage}</span>}
            />
        </div>)
    }

    renderFeatureName(theme) {
        if (this.state.drawnFeature && this.state.schema.properties.name !== undefined) {
            return (<div className={theme.controlGroup}>
                <TextField
                    id={ids.GEO_BAR_DIGITIZE_DRAWN_FEATURE_NAME}
                    required
                    className={theme.textField}
                    label="Feature Name"
                    value={this.state.drawnFeatureName}
                    onChange={evt => this.setState({ drawnFeatureName: evt.target.value })}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    margin="normal"
                />
            </div>)
        }
        return (null)
    }

    renderButtons(theme) {
        return (<React.Fragment>
            <div className={theme.formGroup} title="Undo all modifications since last save">
                <Button
                    classes={{
                        root: theme.buttonSubmit,
                    }}
                    onClick={() => this.cancelEdits()}
                >
                    <span>Cancel</span>
                </Button>
                <Button
                    classes={{
                        root: theme.buttonSubmit,
                    }}
                    disabled={this.isWaiting() || !this.state.drawnFeature}
                    onClick={this.addFeature}
                >
                    <span>Add Feature</span>
                </Button>
                <Button
                    classes={{
                        root: theme.buttonSubmit,
                    }}
                    disabled={!this.submitButtonIsEnabled()}
                    onClick={this.save}
                >
                    <span>Submit</span>
                </Button>
            </div>
        </React.Fragment>)
    }

    renderEditingControls(theme) {
        const editingControls = [{
            key: 'modify',
            forShapeType: 'any',
            label: 'Modify Features',
        }, {
            key: ids.GEO_BAR_DIG_METHOD_DRAW_POINT,
            forShapeType: 'point',
            label: 'Draw a point',
        }, {
            key: ids.GEO_BAR_DIG_METHOD_DRAW_LINE,
            forShapeType: 'line',
            label: 'Draw a Line',
        }, {
            key: ids.GEO_BAR_DIG_METHOD_DRAW_POLYGON,
            forShapeType: 'polygon',
            label: 'Draw a Polygon',
        }, {
            key: ids.GEO_BAR_DIG_METHOD_DRAW_RECTANGLE,
            forShapeType: 'polygon',
            label: 'Draw a Rectangle',
        }, {
            key: ids.GEO_BAR_DIG_METHOD_DRAW_CIRCLE,
            forShapeType: 'polygon',
            label: 'Draw a Circle',
        }]
        const layers = getLayers(this.props.mapsources)
        const editingLayer = layers.find(lyr => lyr.id === this.state.editingLayer)
        let layerType = this.state.newLayerType
        if (editingLayer) {
            layerType = editingLayer.layer_type
        }
        return (
            <Select
                value={this.state.method}
                inputProps={{
                    name: ids.GEO_BAR_DIGITIZE_METHOD,
                    id: ids.GEO_BAR_DIGITIZE_METHOD,
                }}
                onChange={evt => this.setState({ method: evt.target.value })}
            >
                {editingControls.filter(ed => ed.forShapeType === 'any' || layerType.toLowerCase().indexOf(ed.forShapeType) >= 0).map(ed =>
                    (<MenuItem key={ed.key} value={ed.key}>{ed.label}</MenuItem>),
                )}
            </Select>
        )
    }

    renderCreateControls(theme) {
        return (
            <React.Fragment>
                <div className={theme.controlGroup}>
                    <FormControl
                        classes={{
                            root: theme.muiFormControl,
                        }}
                    >
                        <InputLabel htmlFor={'vector_create_options'}>New Layer Type</InputLabel>
                        <Select
                            value={this.state.newLayerType}
                            inputProps={{
                                name: 'vector_create_options',
                                id: 'vector_create_options',
                            }}
                            onChange={(evt) => {
                                this.setState({ newLayerType: evt.target.value })
                            }}
                        >
                            {['Point', 'Line', 'Polygon'].map(type => (
                                <MenuItem key={type} value={type}>{type}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
                <div className={theme.controlGroup}>
                    <TextField
                        id={'new_layer_name'}
                        required
                        className={theme.textField}
                        label="New Layer Name"
                        value={this.state.newLayerName}
                        onChange={evt => this.setState({ newLayerName: evt.target.value })}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        margin="normal"
                    />
                </div>
            </React.Fragment>
        )
    }

    renderTool(theme) {
        const layers = getLayers(this.props.mapsources)
        return (<React.Fragment>
            <div className={theme.controlGroup}>
                <FormControl
                    classes={{
                        root: theme.muiFormControl,
                    }}
                >
                    <InputLabel htmlFor={ids.GEO_BAR_DIGITIZE_FEATURE_SOURCE}>Editing Layer</InputLabel>
                    <Select
                        value={this.state.editingLayer}
                        inputProps={{
                            name: ids.GEO_BAR_BOUNDARY_LAYER,
                            id: ids.GEO_BAR_BOUNDARY_LAYER,
                        }}
                        onChange={(evt) => {
                            const layerId = evt.target.value
                            if (layerId === '__new__') {
                                this.setState({ displayCreate: true, editingLayer: layerId })
                            } else {
                                this.setState({ displayCreate: false, editingLayer: layerId })
                            }
                        }}
                    >
                        <MenuItem key="new" value="__new__">&lt;Create New Layer&gt;</MenuItem>
                        {layers.filter(lyr => lyr.layer_type && lyr.layer_type.toLowerCase() !== 'raster').sort((a, b) => compare(a.name, b.name)).map(layer => (
                            <MenuItem key={layer.id} value={layer.id}>{layer.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>
            {this.state.displayCreate && this.renderCreateControls(theme)}
            <div className={theme.controlGroup}>
                <FormControl
                    classes={{
                        root: theme.muiFormControl,
                    }}
                >
                    <InputLabel htmlFor={ids.GEO_BAR_DIGITIZE_METHOD}>Digitize Method</InputLabel>
                    {this.renderEditingControls(theme)}
                </FormControl>
            </div>
            {this.renderFeatureName(theme)}
            {this.renderButtons(theme)}
        </React.Fragment>)
    }

    render() {
        const theme = this.props.theme || cssStyles
        const helpActive = this.state.helpActive

        return (<div className={theme.geoBarCtr}>
            <div className={theme.formGroup}>
                <span className={theme.toolLabel}>Digitize / Edit Tool</span>
                {this.isWaiting() &&
                    <span><i className="fa fa-refresh fa-spin" /></span>
                }
                <i
                    className={`${theme.helpIcon}
                    ${helpActive ? theme.helpIconActive : ''}
                    fa fa-question-circle`}
                    onClick={() => this.props.actions.onClickInlineHelp(ids.GEO_BAR_DIGITIZE)}
                    role="button"
                    tabIndex={0}
                />
            </div>
            {helpActive &&
            <div className={theme.helpInlineCtr}>
                <div className={theme.helpInline}>
                    <p>
                        Create and edit layers.
                    </p>
                </div>
            </div>
            }
            <div className={theme.toolCtr}>
                {this.renderTool(theme)}
            </div>
            {this.renderNotifications(theme)}
        </div>)
    }
}

export default GeoBarDigitize
